import React, { useState, useEffect } from "react";
import axios from "axios";
import { Landmark, Wallet, PiggyBank, Plus, Trash2, X, CheckCircle2, ArrowDownLeft, ArrowUpRight, ArrowRightLeft, Pencil, Info, Printer, TrendingUp, TrendingDown, Activity, HandCoins, FileSpreadsheet, Loader2 } from "lucide-react";
import { useBranding } from "../hooks/useBranding";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

const TIPE_ICON = { tunai: Wallet, bank: Landmark, cadangan: PiggyBank, prive: HandCoins };
const TIPE_ACCENT = {
  tunai: "from-emerald-600/20 to-emerald-500/5 border-emerald-500/40 text-emerald-300",
  bank: "from-cyan-600/20 to-cyan-500/5 border-cyan-500/40 text-cyan-300",
  cadangan: "from-purple-600/20 to-purple-500/5 border-purple-500/40 text-purple-300",
  prive: "from-amber-600/20 to-amber-500/5 border-amber-500/40 text-amber-300",
};

export const KasBankPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editSaldo, setEditSaldo] = useState(null);
  const [saldoVal, setSaldoVal] = useState(0);
  const [notif, setNotif] = useState("");
  const currentMonthKode = new Date().toISOString().slice(0, 7);
  const [periode, setPeriode] = useState(currentMonthKode);
  const [filterJenis, setFilterJenis] = useState("ALL");
  const [mitraList, setMitraList] = useState([]);
  const [detail, setDetail] = useState(null); // { title, subtitle, rows }
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState({
    jenis: "masuk", akun_id: "", akun_tujuan_id: "", nominal: 0,
    tanggal: new Date().toISOString().split("T")[0], keterangan: "", kategori: "", mitra_id: "",
  });

  const buildKasQuery = () => {
    const yr = new Date().getFullYear();
    if (periode === "RANGE") {
      return startDate && endDate ? `start=${startDate}&end=${endDate}` : null;
    }
    const sp = periode === "1TAHUN" ? String(yr) : periode;
    return `periode=${encodeURIComponent(sp)}`;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const q = buildKasQuery();
      if (q === null) { setLoading(false); return; } // rentang belum lengkap
      const [res, mitraRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/kas/summary?${q}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/master/mitra`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setData(res.data);
      setMitraList(mitraRes.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const exportExcel = async () => {
    const q = buildKasQuery();
    if (q === null) { alert("Lengkapi tanggal 'Dari' dan 'Sampai' terlebih dahulu."); return; }
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/kas/export?${q}`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `arus-kas-${Date.now()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert("Gagal mengunduh Excel"); } finally { setExporting(false); }
  };

  useEffect(() => { fetchData(); }, [periode, startDate, endDate]);

  const notify = (m) => { setNotif(m); setTimeout(() => setNotif(""), 4000); };
  const akunList = data?.akun || [];
  const branding = useBranding();

  const currentYear = new Date().getFullYear();
  const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  const periodeOptions = monthNames.map((label, idx) => ({
    kode: `${currentYear}-${String(idx + 1).padStart(2, "0")}`,
    label, // nama bulan saja (tanpa tahun) agar bisa dipakai setiap tahun
  }));
  const inPeriode = (tgl) => {
    const t = String(tgl || "").slice(0, 10);
    if (!t) return false;
    if (periode === "SEMUA") return true;
    if (periode === "RANGE") return startDate && endDate ? (t >= startDate && t <= endDate) : true;
    if (periode === "1TAHUN") return t.slice(0, 4) === String(currentYear);
    return t.slice(0, 7) === periode;
  };
  const periodeMutasi = (data?.mutasi || []).filter((m) => inPeriode(m.tanggal));

  // Kartu arus kas mengikuti periode (transfer tidak dihitung sebagai masuk/keluar)
  const cardMasuk = periodeMutasi.filter((m) => m.jenis === "masuk").reduce((s, m) => s + (m.nominal || 0), 0);
  const cardKeluar = periodeMutasi.filter((m) => m.jenis === "keluar").reduce((s, m) => s + (m.nominal || 0), 0);
  const cardNet = cardMasuk - cardKeluar;

  const filteredMutasi = periodeMutasi.filter((m) => filterJenis === "ALL" || m.jenis === filterJenis);
  const totalMasuk = filteredMutasi.filter((m) => m.jenis === "masuk").reduce((s, m) => s + (m.nominal || 0), 0);
  const totalKeluar = filteredMutasi.filter((m) => m.jenis === "keluar").reduce((s, m) => s + (m.nominal || 0), 0);
  const namaBulan = periode === "1TAHUN"
    ? `Tahun ${currentYear}`
    : periode === "SEMUA"
      ? "Semua Tahun"
      : periode === "RANGE"
        ? (startDate && endDate ? `${startDate} s/d ${endDate}` : "Rentang Tanggal")
        : (periodeOptions.find((o) => o.kode === periode)?.label || periode);

  const handlePrint = () => {
    const jenisLabel = { ALL: "Semua Jenis", masuk: "Masuk", keluar: "Keluar", transfer: "Transfer" }[filterJenis];
    const rows = filteredMutasi.map((m, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${m.tanggal || "-"}</td>
        <td>${m.jenis === "masuk" ? "Masuk" : m.jenis === "keluar" ? "Keluar" : "Transfer"}</td>
        <td>${m.jenis === "transfer" ? `${m.akun_nama || "-"} &rarr; ${m.akun_tujuan_nama || "-"}` : (m.akun_nama || "-")}</td>
        <td>${(m.keterangan || "-")}${m.auto ? ` [AUTO: ${m.sumber}]` : ""}</td>
        <td class="num">${m.jenis === "masuk" ? "+" : m.jenis === "keluar" ? "&minus;" : ""}Rp ${Number(m.nominal || 0).toLocaleString("id-ID")}</td>
      </tr>`).join("");
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>Arus Kas - ${namaBulan}</title><style>
      body{font-family:Arial,sans-serif;color:#111;padding:24px;font-size:12px}
      h1{font-size:17px;margin:0 0 4px} p{margin:2px 0;color:#444}
      table{width:100%;border-collapse:collapse;margin-top:14px}
      th,td{border:1px solid #999;padding:6px 8px;text-align:left}
      th{background:#eee} .num{text-align:right;font-family:monospace}
      .summary{margin-top:12px;font-weight:bold}
    </style></head><body>
      <h1>${branding.app_name || "ISP Billing"} &mdash; Laporan Arus Kas &amp; Bank</h1>
      <p>Periode: ${namaBulan} &nbsp;|&nbsp; Jenis: ${jenisLabel}</p>
      <p>Dicetak: ${new Date().toLocaleString("id-ID")}</p>
      <table><thead><tr><th>No</th><th>Tanggal</th><th>Jenis</th><th>Akun</th><th>Keterangan</th><th style="text-align:right">Nominal</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="6" style="text-align:center">Tidak ada data pada filter ini</td></tr>'}</tbody></table>
      <p class="summary">Total Masuk: Rp ${totalMasuk.toLocaleString("id-ID")} &nbsp;|&nbsp; Total Keluar: Rp ${totalKeluar.toLocaleString("id-ID")} &nbsp;|&nbsp; Selisih: Rp ${(totalMasuk - totalKeluar).toLocaleString("id-ID")}</p>
    </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const openAdd = () => {
    setForm({ jenis: "masuk", akun_id: akunList[0]?.id || "", akun_tujuan_id: "", nominal: 0, tanggal: new Date().toISOString().split("T")[0], keterangan: "", kategori: "", mitra_id: "" });
    setShowAdd(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/kas/mutasi`, { ...form, nominal: Number(form.nominal) || 0 }, { withCredentials: true });
      notify("Mutasi kas berhasil dicatat!");
      setShowAdd(false);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Gagal mencatat mutasi"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus mutasi kas ini?")) return;
    try { await axios.delete(`${BACKEND_URL}/api/kas/mutasi/${id}`, { withCredentials: true }); fetchData(); }
    catch (err) { alert("Gagal menghapus"); }
  };

  const saveSaldoAwal = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/kas/akun/${id}`, { saldo_awal: Number(saldoVal) || 0 }, { withCredentials: true });
      setEditSaldo(null); notify("Saldo awal diperbarui!"); fetchData();
    } catch (err) { alert("Gagal memperbarui saldo awal"); }
  };

  const openDetail = (title, rows, subtitle = "") => setDetail({ title, subtitle, rows });

  return (
    <div className="space-y-6" data-testid="kas-bank-page">
      {/* Header + total saldo gabungan */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-[10px] font-bold uppercase tracking-wider">Cash &amp; Bank</span>
            <span className="text-xs text-slate-400">Pantau saldo &amp; arus kas</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Kas &amp; Bank</h1>
          <p className="text-xs text-slate-400 mt-0.5">Total Saldo Gabungan:
            <span className="text-emerald-400 font-black font-mono ml-1" data-testid="total-saldo-gabungan">{rp(data?.total_saldo)}</span>
          </p>
        </div>
        <button onClick={openAdd} data-testid="add-mutasi-btn" className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-cyan-600/30 flex items-center gap-2 transition-all">
          <Plus className="w-4 h-4" /><span>Catat Mutasi Kas</span>
        </button>
      </div>

      {notif && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-xl text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" /><span>{notif}</span>
        </div>
      )}

      {/* Info otomatisasi arus kas */}
      <div className="p-3 bg-cyan-500/5 border border-cyan-500/20 text-slate-400 rounded-xl text-[11px] flex items-start gap-2" data-testid="kas-auto-info">
        <Info className="w-3.5 h-3.5 text-cyan-400 mt-0.5 shrink-0" />
        <span>Otomatis: <b className="text-emerald-300">pendapatan</b> (tagihan lunas &amp; pendapatan lain) via <b className="text-emerald-300">tunai/cash</b> masuk ke <b className="text-emerald-300">Kas Tunai</b>, via <b className="text-cyan-300">transfer/VA/QRIS</b> masuk ke <b className="text-cyan-300">Kas Bank</b>; <b className="text-rose-300">pengeluaran</b> keluar dari akun sumbernya; setoran/penarikan <b className="text-purple-300">Dana Cadangan</b> masuk/keluar dari <b className="text-purple-300">Kas Dana Cadangan</b>. Baris bertanda <b className="text-slate-300">AUTO</b> tidak dapat dihapus di sini.</span>
      </div>

      {/* Filter periode + rentang tanggal + ekspor Excel */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl" data-testid="kas-periode-filter">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-400" />
          <span className="text-xs font-bold text-slate-200">Filter Periode Arus Kas</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={periode}
            onChange={(e) => setPeriode(e.target.value)}
            data-testid="kas-periode-select"
            className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold"
          >
            {periodeOptions.map((o) => (
              <option key={o.kode} value={o.kode}>{o.label}</option>
            ))}
            <option value="1TAHUN">Tampilkan Semua (Tahun Ini)</option>
            <option value="SEMUA">Semua Tahun</option>
            <option value="RANGE">Rentang Tanggal…</option>
          </select>
          {periode === "RANGE" && (
            <div className="flex items-center gap-1.5" data-testid="kas-range-inputs">
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="kas-start-date" className="px-2 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" />
              <span className="text-slate-500 text-xs">s/d</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="kas-end-date" className="px-2 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" />
            </div>
          )}
          {periode !== "RANGE" && <span className="text-[11px] text-slate-400">Periode: {namaBulan}</span>}
          <button onClick={exportExcel} disabled={exporting} data-testid="kas-export-btn" className="px-3 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 flex items-center gap-1.5 disabled:opacity-50 transition-all">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
            Ekspor Excel
          </button>
        </div>
      </div>

      {/* Kartu pemantauan arus kas — klik untuk lihat detail */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="arus-kas-monitor">
        <button type="button" onClick={() => openDetail(`Kas Masuk · ${namaBulan}`, periodeMutasi.filter((m) => m.jenis === "masuk"), "Semua transaksi pemasukan pada periode ini")} className="text-left p-5 rounded-2xl bg-gradient-to-br from-emerald-600/20 to-emerald-500/5 border border-emerald-500/40 shadow-xl hover:border-emerald-400 hover:scale-[1.01] transition-all cursor-pointer" data-testid="arus-kas-masuk">
          <div className="flex items-center justify-between">
            <span className="p-2 rounded-xl bg-black/20 text-emerald-300"><TrendingUp className="w-5 h-5" /></span>
            <ArrowDownLeft className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-xs text-emerald-200/80 font-semibold mt-3">Total Kas Masuk</p>
          <p className="text-2xl font-black font-mono text-white mt-0.5">{rp(cardMasuk)}</p>
          <p className="text-[10px] text-emerald-300/80 mt-1 flex items-center gap-1">Pemasukan pada {namaBulan} · <span className="underline">Lihat detail</span></p>
        </button>
        <button type="button" onClick={() => openDetail(`Kas Keluar · ${namaBulan}`, periodeMutasi.filter((m) => m.jenis === "keluar"), "Semua transaksi pengeluaran pada periode ini")} className="text-left p-5 rounded-2xl bg-gradient-to-br from-rose-600/20 to-rose-500/5 border border-rose-500/40 shadow-xl hover:border-rose-400 hover:scale-[1.01] transition-all cursor-pointer" data-testid="arus-kas-keluar">
          <div className="flex items-center justify-between">
            <span className="p-2 rounded-xl bg-black/20 text-rose-300"><TrendingDown className="w-5 h-5" /></span>
            <ArrowUpRight className="w-4 h-4 text-rose-400" />
          </div>
          <p className="text-xs text-rose-200/80 font-semibold mt-3">Total Kas Keluar</p>
          <p className="text-2xl font-black font-mono text-white mt-0.5">{rp(cardKeluar)}</p>
          <p className="text-[10px] text-rose-300/80 mt-1 flex items-center gap-1">Pengeluaran pada {namaBulan} · <span className="underline">Lihat detail</span></p>
        </button>
        {(() => {
          const net = cardNet;
          const positif = net >= 0;
          return (
            <button type="button" onClick={() => openDetail(`Arus Kas Bersih · ${namaBulan}`, periodeMutasi.filter((m) => m.jenis === "masuk" || m.jenis === "keluar"), "Pemasukan & pengeluaran (transfer tidak dihitung)")} className={`text-left p-5 rounded-2xl bg-gradient-to-br ${positif ? "from-cyan-600/20 to-cyan-500/5 border-cyan-500/40 hover:border-cyan-400" : "from-amber-600/20 to-amber-500/5 border-amber-500/40 hover:border-amber-400"} border shadow-xl hover:scale-[1.01] transition-all cursor-pointer`} data-testid="arus-kas-bersih">
              <div className="flex items-center justify-between">
                <span className={`p-2 rounded-xl bg-black/20 ${positif ? "text-cyan-300" : "text-amber-300"}`}><Activity className="w-5 h-5" /></span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${positif ? "bg-cyan-500/20 text-cyan-300" : "bg-amber-500/20 text-amber-300"}`}>{positif ? "SURPLUS" : "DEFISIT"}</span>
              </div>
              <p className={`text-xs font-semibold mt-3 ${positif ? "text-cyan-200/80" : "text-amber-200/80"}`}>Arus Kas Bersih</p>
              <p className="text-2xl font-black font-mono text-white mt-0.5">{rp(net)}</p>
              <p className={`text-[10px] mt-1 ${positif ? "text-cyan-300/80" : "text-amber-300/80"}`}>Selisih masuk &minus; keluar · <span className="underline">Lihat detail</span></p>
            </button>
          );
        })()}
      </div>

      {/* Kartu sumber dana — klik untuk lihat rincian mutasi akun */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {akunList.map((a) => {
          const Icon = TIPE_ICON[a.tipe] || Wallet;
          const akunRows = periodeMutasi.filter((m) => m.akun_id === a.id || m.akun_tujuan_id === a.id);
          return (
            <div key={a.id} onClick={() => { if (editSaldo !== a.id) openDetail(`${a.nama} · ${namaBulan}`, akunRows, `Semua mutasi masuk/keluar/transfer akun ini · Saldo ${rp(a.saldo)}`); }} className={`p-5 rounded-2xl bg-gradient-to-br ${TIPE_ACCENT[a.tipe] || "from-slate-700/20 to-slate-600/5 border-slate-600 text-slate-200"} border shadow-xl cursor-pointer hover:scale-[1.01] hover:brightness-110 transition-all`} data-testid={`kas-akun-${a.tipe}`}>
              <div className="flex items-center justify-between">
                <span className="p-2 rounded-xl bg-black/20"><Icon className="w-5 h-5" /></span>
                <button onClick={(e) => { e.stopPropagation(); setEditSaldo(a.id); setSaldoVal(a.saldo_awal); }} className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg" data-testid={`edit-saldo-${a.tipe}`} title="Set saldo awal"><Pencil className="w-3.5 h-3.5" /></button>
              </div>
              <p className="text-xs text-slate-300 font-semibold mt-3">{a.nama}</p>
              {editSaldo === a.id ? (
                <div className="mt-1 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <input type="number" value={saldoVal} onChange={(e) => setSaldoVal(e.target.value)} data-testid={`saldo-awal-input-${a.tipe}`} className="w-full px-2 py-1 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white font-mono" />
                  <button onClick={() => saveSaldoAwal(a.id)} data-testid={`save-saldo-${a.tipe}`} className="p-1.5 bg-emerald-600 rounded-lg"><CheckCircle2 className="w-3.5 h-3.5 text-white" /></button>
                  <button onClick={() => setEditSaldo(null)} className="p-1.5 bg-slate-700 rounded-lg"><X className="w-3.5 h-3.5 text-white" /></button>
                </div>
              ) : (
                <p className="text-2xl font-black font-mono text-white mt-0.5" data-testid={`saldo-${a.tipe}`}>{rp(a.saldo)}</p>
              )}
              <div className="flex items-center gap-3 mt-3 text-[10px]">
                <span className="flex items-center gap-1 text-emerald-400"><ArrowDownLeft className="w-3 h-3" /> {rp(a.masuk)}</span>
                <span className="flex items-center gap-1 text-rose-400"><ArrowUpRight className="w-3 h-3" /> {rp(a.keluar)}</span>
                <span className="text-slate-500">awal {rp(a.saldo_awal)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Riwayat arus kas */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <h3 className="text-sm font-bold text-white">Riwayat Arus Kas <span className="text-slate-500 font-normal">· {namaBulan}</span></h3>
          <div className="flex flex-wrap items-center gap-2">
            <select value={filterJenis} onChange={(e) => setFilterJenis(e.target.value)} data-testid="filter-jenis-select" className="px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200">
              <option value="ALL">Semua Jenis</option>
              <option value="masuk">Masuk</option>
              <option value="keluar">Keluar</option>
              <option value="transfer">Transfer</option>
            </select>
            <button onClick={handlePrint} disabled={filteredMutasi.length === 0} data-testid="print-arus-kas-btn" className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
              <Printer className="w-3.5 h-3.5" /> Cetak ({filteredMutasi.length})
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 font-semibold">
                <th className="py-2.5 px-3">Tanggal</th>
                <th className="py-2.5 px-3">Jenis</th>
                <th className="py-2.5 px-3">Akun</th>
                <th className="py-2.5 px-3">Keterangan</th>
                <th className="py-2.5 px-3 text-right">Nominal</th>
                <th className="py-2.5 px-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filteredMutasi.map((m) => (
                <tr key={m.id} className="hover:bg-slate-900/40" data-testid={`mutasi-row-${m.id}`}>
                  <td className="py-3 px-3 text-slate-300">{m.tanggal}</td>
                  <td className="py-3 px-3">
                    {m.jenis === "masuk" && <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-bold text-[10px] flex items-center gap-1 w-fit"><ArrowDownLeft className="w-3 h-3" /> Masuk</span>}
                    {m.jenis === "keluar" && <span className="px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 font-bold text-[10px] flex items-center gap-1 w-fit"><ArrowUpRight className="w-3 h-3" /> Keluar</span>}
                    {m.jenis === "transfer" && <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold text-[10px] flex items-center gap-1 w-fit"><ArrowRightLeft className="w-3 h-3" /> Transfer</span>}
                    {m.jenis === "prive" && <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold text-[10px] flex items-center gap-1 w-fit"><HandCoins className="w-3 h-3" /> Prive/Dividen</span>}
                  </td>
                  <td className="py-3 px-3 text-slate-200">
                    {(m.jenis === "transfer" || m.jenis === "prive") ? <span>{m.akun_nama} <span className="text-slate-500">→</span> {m.akun_tujuan_nama}</span> : m.akun_nama}
                  </td>
                  <td className="py-3 px-3 text-slate-400">
                    {m.keterangan || "-"}
                    {m.auto && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">{m.sumber}</span>}
                  </td>
                  <td className={`py-3 px-3 text-right font-mono font-bold ${m.jenis === "masuk" ? "text-emerald-400" : m.jenis === "keluar" ? "text-rose-400" : "text-amber-400"}`}>
                    {m.jenis === "masuk" ? "+" : (m.jenis === "keluar" || m.jenis === "prive") ? "−" : ""}{rp(m.nominal)}
                  </td>
                  <td className="py-3 px-3 text-right">
                    {m.auto ? (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400 border border-slate-600" title={`Otomatis dari ${m.sumber}`}>AUTO</span>
                    ) : (
                      <button onClick={() => handleDelete(m.id)} className="p-1.5 text-rose-400 hover:bg-rose-950/60 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && filteredMutasi.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-500">{filterJenis !== "ALL" || periode !== "1TAHUN" ? "Tidak ada mutasi pada filter ini." : "Belum ada mutasi kas. Klik \"Catat Mutasi Kas\" untuk mulai."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal detail isi kartu */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-3xl shadow-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="kas-detail-modal">
            <div className="flex justify-between items-start border-b border-slate-800 p-5">
              <div>
                <h3 className="text-sm font-bold text-white">{detail.title}</h3>
                {detail.subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{detail.subtitle}</p>}
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-white" data-testid="close-detail-btn"><X className="w-4 h-4" /></button>
            </div>
            {(() => {
              const rows = detail.rows || [];
              const sumMasuk = rows.filter((m) => m.jenis === "masuk").reduce((s, m) => s + (m.nominal || 0), 0);
              const sumKeluar = rows.filter((m) => m.jenis === "keluar" || m.jenis === "prive").reduce((s, m) => s + (m.nominal || 0), 0);
              const sumTransfer = rows.filter((m) => m.jenis === "transfer").reduce((s, m) => s + (m.nominal || 0), 0);
              return (
                <>
                  <div className="grid grid-cols-3 gap-2 p-4 border-b border-slate-800">
                    <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/25"><p className="text-[9px] text-emerald-400">Masuk</p><p className="text-xs font-bold font-mono text-emerald-300">{rp(sumMasuk)}</p></div>
                    <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/25"><p className="text-[9px] text-rose-400">Keluar</p><p className="text-xs font-bold font-mono text-rose-300">{rp(sumKeluar)}</p></div>
                    <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/25"><p className="text-[9px] text-amber-400">Transfer</p><p className="text-xs font-bold font-mono text-amber-300">{rp(sumTransfer)}</p></div>
                  </div>
                  <div className="overflow-y-auto p-4">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-slate-800 text-slate-400 font-semibold sticky top-0 bg-[#0f172a]">
                          <th className="py-2 px-2">Tanggal</th>
                          <th className="py-2 px-2">Jenis</th>
                          <th className="py-2 px-2">Akun</th>
                          <th className="py-2 px-2">Keterangan</th>
                          <th className="py-2 px-2 text-right">Nominal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/60">
                        {rows.map((m) => (
                          <tr key={m.id} className="hover:bg-slate-900/40" data-testid={`detail-row-${m.id}`}>
                            <td className="py-2.5 px-2 text-slate-300 whitespace-nowrap">{m.tanggal}</td>
                            <td className="py-2.5 px-2">
                              {m.jenis === "masuk" && <span className="text-emerald-300 font-bold">Masuk</span>}
                              {m.jenis === "keluar" && <span className="text-rose-300 font-bold">Keluar</span>}
                              {m.jenis === "transfer" && <span className="text-amber-300 font-bold">Transfer</span>}
                              {m.jenis === "prive" && <span className="text-amber-300 font-bold">Prive</span>}
                            </td>
                            <td className="py-2.5 px-2 text-slate-200">{(m.jenis === "transfer" || m.jenis === "prive") ? <span>{m.akun_nama} <span className="text-slate-500">→</span> {m.akun_tujuan_nama}</span> : m.akun_nama}</td>
                            <td className="py-2.5 px-2 text-slate-400">{m.keterangan || "-"}{m.auto && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">{m.sumber}</span>}</td>
                            <td className={`py-2.5 px-2 text-right font-mono font-bold whitespace-nowrap ${m.jenis === "masuk" ? "text-emerald-400" : (m.jenis === "keluar" || m.jenis === "prive") ? "text-rose-400" : "text-amber-400"}`}>{m.jenis === "masuk" ? "+" : (m.jenis === "keluar" || m.jenis === "prive") ? "−" : ""}{rp(m.nominal)}</td>
                          </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-500">Tidak ada transaksi pada periode ini.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <div className="p-3 border-t border-slate-800 text-right text-[11px] text-slate-400">Total {rows.length} transaksi</div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Modal catat mutasi */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4" data-testid="mutasi-modal">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white">Catat Mutasi Kas</h3>
              <button onClick={() => setShowAdd(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-300 mb-1">Jenis Mutasi</label>
                <div className="grid grid-cols-2 gap-2">
                  {[["masuk", "Uang Masuk"], ["keluar", "Uang Keluar"], ["transfer", "Transfer"], ["prive", "Prive / Dividen"]].map(([v, l]) => (
                    <button type="button" key={v} onClick={() => setForm({ ...form, jenis: v })} data-testid={`jenis-${v}`}
                      className={`px-2 py-1.5 rounded-xl text-[11px] font-bold border ${form.jenis === v ? "bg-cyan-600 border-cyan-500 text-white" : "bg-slate-900 border-slate-700 text-slate-300"}`}>{l}</button>
                  ))}
                </div>
              </div>
              {form.jenis === "prive" && (
                <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200 flex items-start gap-2">
                  <HandCoins className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Pengambilan modal/dividen. Saldo Kas/Bank sumber berkurang & tercatat ke akun ekuitas <b>Prive</b>. <b>Tidak</b> masuk Laba Rugi / pengeluaran operasional — hanya mengurangi Ekuitas di Neraca.</span>
                </div>
              )}
              {form.jenis === "prive" && (
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Atas Nama (Mitra/Investor) — opsional</label>
                  <select value={form.mitra_id} onChange={(e) => setForm({ ...form, mitra_id: e.target.value })} data-testid="prive-mitra-select" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                    <option value="">Pemilik / Owner (umum)</option>
                    {mitraList.map((mt) => <option key={mt.id} value={mt.id}>{mt.nama}</option>)}
                  </select>
                  <p className="text-[10px] text-amber-400/80 mt-1">Pilih mitra agar prive tercatat atas namanya di rincian Laporan Perubahan Modal.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-300 mb-1">{form.jenis === "transfer" ? "Dari Akun" : form.jenis === "prive" ? "Dari Akun (Kas/Bank)" : "Akun"} *</label>
                  <select value={form.akun_id} onChange={(e) => setForm({ ...form, akun_id: e.target.value })} required data-testid="mutasi-akun-select" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                    {akunList.filter((a) => form.jenis === "prive" ? (a.tipe === "tunai" || a.tipe === "bank") : a.tipe !== "prive").map((a) => <option key={a.id} value={a.id}>{a.nama}</option>)}
                  </select>
                </div>
                {form.jenis === "transfer" ? (
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Ke Akun *</label>
                    <select value={form.akun_tujuan_id} onChange={(e) => setForm({ ...form, akun_tujuan_id: e.target.value })} required data-testid="mutasi-akun-tujuan-select" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                      <option value="">-- Pilih --</option>
                      {akunList.filter((a) => a.id !== form.akun_id && a.tipe !== "prive").map((a) => <option key={a.id} value={a.id}>{a.nama}</option>)}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs text-slate-300 mb-1">Tanggal</label>
                    <input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" />
                  </div>
                )}
              </div>
              {form.jenis === "transfer" && (
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Tanggal</label>
                  <input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" />
                </div>
              )}
              <div>
                <label className="block text-xs text-slate-300 mb-1">Nominal (Rp) *</label>
                <input type="number" required value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} data-testid="mutasi-nominal-input" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono" />
              </div>
              {form.jenis === "masuk" && (
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Kategori</label>
                  <select value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })} data-testid="mutasi-kategori-select" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100">
                    <option value="">Umum / Operasional</option>
                    <option value="Modal Disetor">Modal Disetor (setoran modal pemilik/investor)</option>
                  </select>
                  {form.kategori === "Modal Disetor" && <p className="text-[10px] text-cyan-400/80 mt-1">Tercatat sebagai penambah Ekuitas (modal) di Neraca — bukan pendapatan operasional.</p>}
                </div>
              )}
              <div>
                <label className="block text-xs text-slate-300 mb-1">Keterangan</label>
                <input type="text" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} placeholder="cth: Setoran tunai ke bank" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100" />
              </div>
              <div className="pt-2 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs">Batal</button>
                <button type="submit" data-testid="submit-mutasi-btn" className="px-4 py-1.5 bg-cyan-600 text-white rounded-xl text-xs font-bold">Simpan Mutasi</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
